// K212 / BUSINESS-82 (Owner 12 Okt 2026): "penanda upah borongan "sudah dibayar" diisi Finance per periode per
// cabang, dengan bukti."
//
// Finance menandai satu periode rekap satu cabang: tanggal bayar + bukti (wajib) + catatan. Tanda tidak bisa diubah;
// pembatalan hanya Finance dengan alasan. Owner dan Kepala Cabang (cabangnya) melihat status + bukti.
// Seluruh pagar ada di server (services/upah_dibayar.py) — layar ini hanya menyembunyikan tombol.
import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";

const rupiah = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const tgl = (s) => new Date(`${String(s).slice(0, 10)}T00:00:00`)
  .toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
const hariIni = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const BUKTI_MIME = "image/jpeg,image/png,image/webp,application/pdf";

export default function TandaDibayarUpah({ periode, dibayar, bolehTandai, onBerubah }) {
  const [buka, setBuka] = useState(false);
  const [tanggal, setTanggal] = useState(hariIni());
  const [berkas, setBerkas] = useState(null);
  const [catatan, setCatatan] = useState("");
  const [batal, setBatal] = useState(false);
  const [alasan, setAlasan] = useState("");
  const [sibuk, setSibuk] = useState(false);

  if (!periode) return null;
  const belumSelesai = periode.selesai >= hariIni();

  async function lihatBukti() {
    const tab = window.open("", "_blank");   // dibuka DULU — sesudah await diblokir peramban
    try {
      const r = await api.get(`/borongan/dibayar/${dibayar.id}/bukti`);
      if (!r.data.signed_url) throw new Error("Bukti tidak tersedia.");
      if (tab) tab.location.href = r.data.signed_url; else window.location.href = r.data.signed_url;
    } catch (e) {
      if (tab) tab.close();
      toast.error(apiErrorMessage(e, "Bukti gagal dibuka."));
    }
  }

  async function simpan() {
    if (!berkas) { toast.error("Bukti pembayaran wajib diunggah."); return; }
    const fd = new FormData();
    fd.append("tanggal_bayar", tanggal);
    if (catatan.trim()) fd.append("catatan", catatan.trim());
    fd.append("file", berkas);
    setSibuk(true);
    try {
      await api.post(`/borongan/periode/${periode.id}/dibayar`, fd);
      toast.success("Upah periode ini ditandai sudah dibayar.");
      setBuka(false); setBerkas(null); setCatatan("");
      onBerubah && onBerubah();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Tanda sudah dibayar ditolak."));
    } finally {
      setSibuk(false);
    }
  }

  async function batalkan() {
    setSibuk(true);
    try {
      await api.post(`/borongan/periode/${periode.id}/dibayar/batal`, { alasan: alasan.trim() });
      toast.success("Tanda sudah dibayar dibatalkan.");
      setBatal(false); setAlasan("");
      onBerubah && onBerubah();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Pembatalan ditolak."));
    } finally {
      setSibuk(false);
    }
  }

  if (dibayar) {
    return (
      <div className="rounded-xl border border-border bg-card p-4 text-sm space-y-2" data-testid="upah-dibayar">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-medium text-foreground" data-testid="upah-dibayar-status">
            Sudah dibayar {tgl(dibayar.tanggal_bayar)} oleh {dibayar.ditandai_oleh_nama || "Finance"}
          </span>
          <span className="tabular-nums text-muted-foreground">Total dibayar {rupiah(dibayar.total_idr)}</span>
        </div>
        {dibayar.catatan && <p className="text-muted-foreground">Catatan: {dibayar.catatan}</p>}
        <div className="flex flex-wrap gap-3">
          {dibayar.ada_bukti && (
            <button type="button" className="underline" onClick={lihatBukti} data-testid="upah-dibayar-bukti">
              Lihat bukti pembayaran
            </button>
          )}
          {bolehTandai && !batal && (
            <button type="button" className="text-destructive underline" onClick={() => setBatal(true)}
                    data-testid="upah-dibayar-batal">
              Batalkan tanda
            </button>
          )}
        </div>
        {batal && (
          <div className="flex flex-wrap items-end gap-2">
            <label className="block grow">
              <span className="mb-1 block text-xs text-muted-foreground">Alasan pembatalan (minimal 10 huruf)</span>
              <input type="text" className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                     value={alasan} onChange={(e) => setAlasan(e.target.value)} data-testid="upah-dibayar-alasan" />
            </label>
            <button type="button" disabled={sibuk || alasan.trim().length < 10}
                    className="rounded-lg border border-input px-3 py-2 text-sm" onClick={batalkan}>
              Simpan pembatalan
            </button>
            <button type="button" className="px-3 py-2 text-sm text-muted-foreground" onClick={() => setBatal(false)}>
              Tutup
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4 text-sm space-y-2" data-testid="upah-belum-dibayar">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-muted-foreground">
          {belumSelesai
            ? "Periode ini belum selesai. Upahnya bisa ditandai sudah dibayar sesudah tanggal akhir periode lewat."
            : "Upah periode ini belum ditandai sudah dibayar."}
        </span>
        {bolehTandai && !belumSelesai && !buka && (
          <button type="button" className="rounded-lg border border-input px-3 py-2 text-sm"
                  onClick={() => setBuka(true)} data-testid="upah-tandai-dibayar">
            Tandai sudah dibayar
          </button>
        )}
      </div>
      {buka && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Tanggal bayar</span>
            <input type="date" className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
                   value={tanggal} min={periode.mulai} max={hariIni()}
                   onChange={(e) => setTanggal(e.target.value)} data-testid="upah-dibayar-tanggal" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Bukti transfer / tanda terima (JPG, PNG, PDF; maks 5 MB)</span>
            <input type="file" accept={BUKTI_MIME} className="text-sm"
                   onChange={(e) => setBerkas(e.target.files?.[0] || null)} data-testid="upah-dibayar-berkas" />
          </label>
          <label className="block grow">
            <span className="mb-1 block text-xs text-muted-foreground">Catatan (boleh kosong)</span>
            <input type="text" className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                   value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="upah-dibayar-catatan" />
          </label>
          <button type="button" disabled={sibuk || !tanggal || !berkas}
                  className="rounded-lg border border-input px-3 py-2 text-sm" onClick={simpan}
                  data-testid="upah-dibayar-simpan">
            Simpan
          </button>
          <button type="button" className="px-3 py-2 text-sm text-muted-foreground" onClick={() => setBuka(false)}>
            Batal
          </button>
          <p className="basis-full text-xs text-muted-foreground">
            Tanda ini tidak bisa diubah. Bila salah, batalkan dengan alasan lalu tandai ulang. Koreksi tarif yang
            disetujui sesudah ini masuk ke periode berikutnya.
          </p>
        </div>
      )}
    </div>
  );
}
